import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPatch, apiPost, getApiError } from '../../lib/api';
import { uploadModelImage } from '../../lib/upload';
import { Modal } from '../../components/Modal';
import { Input } from '../../components/Input';
import { Button } from '../../components/Button';
import { IconButton } from '../../components/IconButton';
import { VatPricePair } from '../../components/VatPricePair';
import { cn } from '../../lib/cn';
import { useHasPermission } from '../../lib/permissions';

// Variants are created at the Mauritius standard rate (15%); the wizard uses it
// to convert between excl./incl.-VAT price entry. Stored prices are incl. VAT.
const WIZARD_VAT = 0.15;

const STANDARD_SIZES = [
  '90*190',
  '107*190',
  '140*190',
  '150*190',
  '150*200',
  '160*200',
  '180*200',
  '200*200',
];
const SUGGESTED_COLORS = ['blue', 'brown', 'red', 'grena'];
const SUGGESTED_SIDES = ['gris', 'blanc'];
const SUGGESTED_FABRICS = ['velvet', 'linen', 'leatherette', 'cotton'];
const SUGGESTED_DENSITIES = ['d23', 'd28', 'd32', 'd40'];

// Finish axes — colour, side colour, fabric and density. Every finish is a
// PRICED variant axis (own SKU + price/stock per value); there is no free
// order-time choice. Mirrors FINISH_AXES in the shared schema. Size is always
// priced too and not listed here. All four are always available on any
// finish-bearing model.
type FinishAxis = 'color' | 'sideColor' | 'fabric' | 'density';
const FINISH_META: { axis: FinishAxis; label: string; col: string; suggestions: string[] }[] = [
  { axis: 'color', label: 'Mattress colours', col: 'Colour', suggestions: SUGGESTED_COLORS },
  { axis: 'sideColor', label: 'Side colours', col: 'Side', suggestions: SUGGESTED_SIDES },
  { axis: 'fabric', label: 'Fabrics', col: 'Fabric', suggestions: SUGGESTED_FABRICS },
  { axis: 'density', label: 'Densities', col: 'Density', suggestions: SUGGESTED_DENSITIES },
];
const ALL_FINISH_AXES: FinishAxis[] = FINISH_META.map((m) => m.axis);

interface Category {
  id: string;
  name: string;
  code: string;
  fulfillmentStrategy: string;
  tracksStockPerLocation: boolean;
}
interface StockLocation {
  id: string;
  name: string;
  kind: string;
  shop: { code: string; name: string } | null;
}
interface ModelDetail {
  id: string;
  name: string;
  categoryId: string;
  description: string | null;
  gift: string | null;
  warranty: string | null;
  colorOptions: string[];
  sideColorOptions: string[];
  fabricOptions: string[];
  densityOptions: string[];
  pricedAxes: string[];
  gifts: Array<{
    // Fixed-variant gift: productId set; model gift: giftModelId set (size chosen
    // at order time). Exactly one of the two.
    productId: string | null;
    giftModelId: string | null;
    qty: number;
    name: string;
  }>;
  variants: Array<{
    id: string;
    size: string | null;
    fabric: string | null;
    color: string | null;
    sideColor: string | null;
    density: string | null;
    sku: string;
    barcode: string | null;
    basePrice: string;
    salePrice: string | null;
    stockLevels: Array<{ onHand: number; location: { id: string } }>;
  }>;
}

interface GiftRow {
  // A fixed-variant gift carries productId; a model gift (any size, chosen at
  // order time) carries giftModelId. Exactly one is set.
  productId: string | null;
  giftModelId: string | null;
  qty: number;
  name: string;
}

interface VariantRow {
  id?: string; // set once persisted (Product id)
  size: string;
  // Priced finish axes — picked per row from the value lists. Imported uses
  // `fabric`; made-to-order uses whichever axes the model marks as priced.
  fabric: string;
  color: string;
  sideColor: string;
  density: string;
  sku: string;
  barcode: string;
  basePrice: string; // incl. VAT
  salePrice: string; // incl. VAT
}
const emptyRow = (size = '', extra: Partial<VariantRow> = {}): VariantRow => ({
  size,
  fabric: '',
  color: '',
  sideColor: '',
  density: '',
  sku: '',
  barcode: '',
  basePrice: '',
  salePrice: '',
  ...extra,
});

const STEPS = ['Product', 'Sizes', 'Stock'] as const;
// In EDIT mode the three steps become freely-navigable tabs (not a linear
// wizard), with edit-oriented labels: Details · Models · Stock.
const EDIT_TABS = ['Details', 'Models', 'Stock'] as const;

export function ProductModelWizard({
  onClose,
  editModelId,
  startStep = 1,
}: {
  onClose: () => void;
  /** An existing model to load — edit (from Products) or resume (incomplete draft). */
  editModelId?: string;
  /** Which step to open on. Edit → 1 (default); resume a draft → 2. */
  startStep?: number;
}): JSX.Element {
  const qc = useQueryClient();
  const cats = useQuery({
    queryKey: ['cats-wizard'],
    queryFn: () => apiGet<Category[]>('/categories'),
  });

  const isEdit = !!editModelId;
  const [step, setStep] = useState<number>(1);
  const [modelId, setModelId] = useState<string | null>(editModelId ?? null);
  const [error, setError] = useState<string | null>(null);
  // Edit mode: brief "Saved ✓" confirmation after a section save (since saving
  // no longer advances/closes the way the create wizard does).
  const [savedFlash, setSavedFlash] = useState(false);
  const flashSaved = (): void => {
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 2500);
  };

  // Step 1 — model basics
  const [name, setName] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [description, setDescription] = useState('');
  const [gift, setGift] = useState('');
  const [gifts, setGifts] = useState<GiftRow[]>([]);
  const [warranty, setWarranty] = useState('');
  const [colors, setColors] = useState<string[]>([]);
  const [sideColors, setSideColors] = useState<string[]>([]);
  // Fabrics: for made-to-order these are order-time options (model.fabricOptions);
  // for imported they're the list to pick from per size row (each = its own SKU).
  const [fabrics, setFabrics] = useState<string[]>([]);
  // Densities: order-time options (model.densityOptions) or a priced per-SKU axis,
  // mirroring fabric.
  const [densities, setDensities] = useState<string[]>([]);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageDone, setImageDone] = useState(false);

  // Step 2 — variants
  const [rows, setRows] = useState<VariantRow[]>([emptyRow()]);

  // Step 3 — opening stock grid, keyed `${productId}::${locationId}` → qty string
  const [grid, setGrid] = useState<Record<string, string>>({});

  // Stock editing on the catalogue is gated by stock.adjust. Roles without it
  // (e.g. ops) skip the opening-stock step — they set stock on the Stock page.
  const canAdjustStock = useHasPermission('stock.adjust');
  const category = (cats.data ?? []).find((c) => c.id === categoryId);
  const madeToOrder = category?.fulfillmentStrategy.startsWith('MADE_TO_ORDER') ?? false;
  const stockTracked = category?.tracksStockPerLocation ?? false;
  const lastStep = stockTracked && canAdjustStock ? 3 : 2;
  // Any chosen category carries finishes (colour / side colour / fabric /
  // density). Every finish is always a priced variant axis — each value is its
  // own SKU with price & stock — configured in the Sizes step (step 2).
  const pricedAxes: FinishAxis[] = category ? ALL_FINISH_AXES : [];

  const isPriced = (axis: FinishAxis): boolean => pricedAxes.includes(axis);
  const axisValues: Record<FinishAxis, string[]> = {
    color: colors,
    sideColor: sideColors,
    fabric: fabrics,
    density: densities,
  };
  const setAxisValues: Record<FinishAxis, (v: string[]) => void> = {
    color: setColors,
    sideColor: setSideColors,
    fabric: setFabrics,
    density: setDensities,
  };
  // Show a grid column for a priced axis once it has at least one value to pick.
  const showCol = (axis: FinishAxis): boolean => isPriced(axis) && axisValues[axis].length > 0;
  const pricedCols = FINISH_META.filter((m) => showCol(m.axis));
  // Every priced axis (even before values are entered) — drives the step-2 editors.
  const pricedAxesList = FINISH_META.filter((m) => isPriced(m.axis));

  // Picking a category just records it — every finish is priced regardless of the
  // fulfilment strategy (pricedAxes is derived above), so there are no defaults
  // to seed.
  const pickCategory = (id: string): void => setCategoryId(id);

  useEffect(
    () => () => {
      if (imagePreview) URL.revokeObjectURL(imagePreview);
    },
    [imagePreview],
  );
  const pickImage = (file: File | null): void => {
    setImageFile(file);
    setImageDone(false);
    setImagePreview(file ? URL.createObjectURL(file) : null);
  };

  // Model detail (for resume + the stock grid). Refetched after each save.
  const detail = useQuery({
    queryKey: ['wizard-model', modelId],
    queryFn: () => apiGet<ModelDetail>(`/product-models/${modelId}`),
    enabled: !!modelId,
  });

  // One-time hydration when editing / resuming an existing model.
  const hydrated = useRef(false);
  useEffect(() => {
    if (hydrated.current || !editModelId || !detail.data) return;
    hydrated.current = true;
    const m = detail.data;
    setName(m.name);
    setCategoryId(m.categoryId);
    setDescription(m.description ?? '');
    setGift(m.gift ?? '');
    setGifts(
      (m.gifts ?? []).map((g) => ({
        productId: g.productId,
        giftModelId: g.giftModelId,
        qty: g.qty,
        name: g.name,
      })),
    );
    setWarranty(m.warranty ?? '');
    // Per-axis values present on the variants. Seed each finish's value list from
    // the UNION of any stored order-time options (legacy free choices) and the
    // values already on its priced variants, so nothing is lost when an existing
    // model is opened (all axes are now priced).
    const variantVals: Record<FinishAxis, string[]> = {
      color: uniq(m.variants.map((v) => v.color).filter(Boolean) as string[]),
      sideColor: uniq(m.variants.map((v) => v.sideColor).filter(Boolean) as string[]),
      fabric: uniq(m.variants.map((v) => v.fabric).filter(Boolean) as string[]),
      density: uniq(m.variants.map((v) => v.density).filter(Boolean) as string[]),
    };
    setColors(uniq([...m.colorOptions, ...variantVals.color]));
    setSideColors(uniq([...m.sideColorOptions, ...variantVals.sideColor]));
    setFabrics(uniq([...m.fabricOptions, ...variantVals.fabric]));
    setDensities(uniq([...m.densityOptions, ...variantVals.density]));
    setRows(m.variants.length ? m.variants.map(variantToRow) : [emptyRow()]);
    setGrid(buildGrid(m.variants));
    setStep(startStep);
  }, [editModelId, detail.data, startStep]);

  const locations = useQuery({
    queryKey: ['stock-locations'],
    queryFn: () => apiGet<StockLocation[]>('/stock/locations'),
    enabled: stockTracked,
  });

  const setRow = (i: number, patch: Partial<VariantRow>): void =>
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addRow = (size = ''): void => setRows((rs) => [...rs, emptyRow(size)]);
  const addStandardSizes = (): void => {
    const existing = new Set(rows.map((r) => r.size));
    const toAdd = STANDARD_SIZES.filter((s) => !existing.has(s)).map((s) => emptyRow(s));
    setRows((rs) => {
      const base = rs.length === 1 && !rs[0]?.size && !rs[0]?.sku ? [] : rs;
      return [...base, ...toAdd];
    });
  };
  // Fill out every size × priced-finish combination not already in the grid, each
  // as a blank row (SKU + price for the admin to enter). Deduped by full key.
  const genCombos = (): void => {
    const sizeDim = uniq(rows.map((r) => r.size.trim()).filter(Boolean));
    const dims = {
      size: sizeDim.length ? sizeDim : [''],
      color: showCol('color') ? colors : [''],
      sideColor: showCol('sideColor') ? sideColors : [''],
      fabric: showCol('fabric') ? fabrics : [''],
      density: showCol('density') ? densities : [''],
    };
    const keyOf = (s: string, c: string, sd: string, f: string, d: string): string =>
      [s, c, sd, f, d].join('§');
    const have = new Set(
      rows.map((r) =>
        keyOf(r.size.trim(), r.color.trim(), r.sideColor.trim(), r.fabric.trim(), r.density.trim()),
      ),
    );
    // Price is set by size + fabric + density, NOT by colour/side-colour — so a
    // colour variant inherits the price already entered for its size+fabric
    // sibling. Build that lookup from existing priced rows; first one wins.
    const priceKeyOf = (s: string, f: string, d: string): string => [s, f, d].join('§');
    const priceByKey = new Map<string, { basePrice: string; salePrice: string }>();
    for (const r of rows) {
      if (r.basePrice === '' && r.salePrice === '') continue;
      const pk = priceKeyOf(r.size.trim(), r.fabric.trim(), r.density.trim());
      if (!priceByKey.has(pk)) priceByKey.set(pk, { basePrice: r.basePrice, salePrice: r.salePrice });
    }
    const additions: VariantRow[] = [];
    for (const s of dims.size)
      for (const c of dims.color)
        for (const sd of dims.sideColor)
          for (const f of dims.fabric)
            for (const d of dims.density) {
              const k = keyOf(s, c, sd, f, d);
              if (have.has(k)) continue;
              have.add(k);
              const price = priceByKey.get(priceKeyOf(s, f, d));
              additions.push(
                emptyRow(s, {
                  color: c,
                  sideColor: sd,
                  fabric: f,
                  density: d,
                  ...(price ?? {}),
                }),
              );
            }
    // Back-fill existing rows that share a size+fabric+density with a priced
    // sibling but were left blank (e.g. colour variants created earlier) — never
    // overwrite a price that's already been typed.
    const fillBlanks = (r: VariantRow): VariantRow => {
      if (r.basePrice !== '' || r.salePrice !== '') return r;
      const price = priceByKey.get(priceKeyOf(r.size.trim(), r.fabric.trim(), r.density.trim()));
      return price ? { ...r, ...price } : r;
    };
    if (additions.length === 0 && priceByKey.size === 0) return;
    setRows((rs) => {
      const blankFirst =
        rs.length === 1 &&
        !rs[0]?.size &&
        !rs[0]?.sku &&
        !rs[0]?.color &&
        !rs[0]?.sideColor &&
        !rs[0]?.fabric &&
        !rs[0]?.density;
      return [...(blankFirst ? [] : rs.map(fillBlanks)), ...additions];
    });
  };

  const savedCount = rows.filter((r) => r.id).length;
  const savedVariants = rows.filter((r): r is VariantRow & { id: string } => !!r.id);
  const newValidRows = rows.filter((r) => !r.id && r.sku.trim() && r.basePrice !== '');
  const allSkus = rows.map((r) => r.sku.trim()).filter(Boolean);
  const dupSkus = new Set(allSkus).size !== allSkus.length;
  // Rows that can receive an auto-generated code: new (unsaved — saved SKUs are
  // permanent) and still blank, so manual entries are never overwritten.
  const blankSkuIdx = rows.map((r, i) => (!r.id && !r.sku.trim() ? i : -1)).filter((i) => i >= 0);

  // ── Auto-fill blank Code/SKU cells with category-prefixed running codes ─────
  // Server-derived (e.g. BED-00042) so numbers don't collide across the company;
  // returned codes are assigned to blank rows in order, admin can still edit.
  const autofillSkus = useMutation({
    mutationFn: async (): Promise<{ i: number; sku: string }[]> => {
      if (blankSkuIdx.length === 0 || !categoryId) return [];
      const { codes } = await apiGet<{ prefix: string; nextNumber: number; codes: string[] }>(
        '/product-models/next-skus',
        { categoryId, count: blankSkuIdx.length },
      );
      return blankSkuIdx.map((i, k) => ({ i, sku: codes[k]! })).filter((a) => a.sku);
    },
    onSuccess: (assignments) => {
      setError(null);
      setRows((rs) => rs.map((r, idx) => {
        const a = assignments.find((x) => x.i === idx);
        return a ? { ...r, sku: a.sku } : r;
      }));
    },
    onError: (e) => setError(getApiError(e).message),
  });

  // ── Step 1: create or update the model, then continue ──────────────────────
  const saveStep1 = useMutation({
    mutationFn: async () => {
      // Only NON-priced (free) finishes are stored as order-time options; a priced
      // finish lives as per-SKU variant rows and is recorded in pricedAxes. Applies
      // to every category — a stocked free finish is one SKU picked on the order.
      const freeOpts = (axis: FinishAxis, vals: string[]): string[] =>
        !isPriced(axis) ? vals : [];
      const body = {
        name: name.trim(),
        categoryId,
        description: description.trim() || null,
        gift: gift.trim() || null,
        // Send only the set target (productId for a fixed variant, giftModelId for
        // a model gift) — the server's giftLinkInputSchema requires exactly one.
        gifts: gifts.map((g) =>
          g.giftModelId
            ? { giftModelId: g.giftModelId, qty: g.qty }
            : { productId: g.productId, qty: g.qty },
        ),
        warranty: warranty.trim() || null,
        colorOptions: freeOpts('color', colors),
        sideColorOptions: freeOpts('sideColor', sideColors),
        fabricOptions: freeOpts('fabric', fabrics),
        densityOptions: freeOpts('density', densities),
        pricedAxes,
      };
      let id = modelId;
      if (!id) {
        const created = await apiPost<{ id: string }>('/product-models', { ...body, variants: [] });
        id = created.id;
        setModelId(id);
      } else {
        await apiPatch(`/product-models/${id}`, body);
      }
      if (imageFile && !imageDone) {
        const url = await uploadModelImage(id, imageFile);
        await apiPost(`/product-models/${id}/images`, { url, isPrimary: true });
        setImageDone(true);
      }
      return id;
    },
    onSuccess: () => {
      setError(null);
      if (isEdit) {
        qc.invalidateQueries({ queryKey: ['products-admin'] });
        qc.invalidateQueries({ queryKey: ['product-models'] });
        flashSaved();
      } else {
        setStep(2);
      }
    },
    onError: (e) => setError(getApiError(e).message),
  });

  // ── Step 2: persist variant rows (update existing + add new); continue ─────
  const saveStep2 = useMutation({
    mutationFn: async () => {
      if (!modelId) throw new Error('Save the product step first.');
      // A row contributes a finish value only on axes that are priced (a column);
      // non-priced axes stay null (free choice / not applicable).
      const axisOf = (
        r: VariantRow,
      ): { fabric: string | null; color: string | null; sideColor: string | null; density: string | null } => ({
        fabric: isPriced('fabric') ? r.fabric.trim() || null : null,
        color: isPriced('color') ? r.color.trim() || null : null,
        sideColor: isPriced('sideColor') ? r.sideColor.trim() || null : null,
        density: isPriced('density') ? r.density.trim() || null : null,
      });
      const nameOf = (r: VariantRow, a: ReturnType<typeof axisOf>): string =>
        [name.trim(), r.size.trim() || null, a.fabric, a.color, a.sideColor, a.density]
          .filter(Boolean)
          .join(' ');
      // Update existing variants — price/size/finishes/barcode. Recompute the name
      // so order/invoice snapshots stay correct. SKU is identity and not changed.
      for (const r of rows) {
        if (!r.id || r.basePrice === '') continue;
        const a = axisOf(r);
        await apiPatch(`/products/${r.id}`, {
          name: nameOf(r, a),
          size: r.size.trim() || null,
          ...a,
          barcode: r.barcode.trim() || null,
          basePrice: Number(r.basePrice),
          salePrice: r.salePrice !== '' ? Number(r.salePrice) : null,
          vatRate: WIZARD_VAT,
        });
      }
      // Add new variants.
      if (newValidRows.length > 0) {
        await apiPost(`/product-models/${modelId}/variants`, {
          variants: newValidRows.map((r) => ({
            size: r.size.trim() || null,
            ...axisOf(r),
            sku: r.sku.trim(),
            barcode: r.barcode.trim() || null,
            basePrice: Number(r.basePrice),
            salePrice: r.salePrice !== '' ? Number(r.salePrice) : null,
            vatRate: WIZARD_VAT,
            openingStock: [],
          })),
        });
      }
      const fresh = await apiGet<ModelDetail>(`/product-models/${modelId}`);
      return fresh;
    },
    onSuccess: (fresh) => {
      setError(null);
      setRows(fresh.variants.map(variantToRow));
      // Build the step-3 grid from the just-saved variants (which carry their
      // per-location stock levels) so the grid is always in sync.
      setGrid(buildGrid(fresh.variants));
      qc.invalidateQueries({ queryKey: ['product-models'] });
      qc.invalidateQueries({ queryKey: ['products-admin'] });
      qc.invalidateQueries({ queryKey: ['wizard-model', modelId] });
      if (isEdit) {
        flashSaved();
      } else if (lastStep === 2) {
        finish();
      } else {
        setStep(3);
      }
    },
    onError: (e) => setError(getApiError(e).message),
  });

  // Remove a variant: deactivate if saved, else drop locally.
  const removeRow = useMutation({
    mutationFn: async (i: number) => {
      const row = rows[i];
      if (row?.id) await apiPost(`/products/${row.id}/deactivate`);
      return i;
    },
    onSuccess: (i) =>
      setRows((rs) => (rs.length === 1 ? [emptyRow()] : rs.filter((_, idx) => idx !== i))),
    onError: (e) => setError(getApiError(e).message),
  });

  // ── Step 3: save opening stock (grid is built when step 2 saves) ───────────
  const saveStep3 = useMutation({
    mutationFn: async () => {
      if (!modelId) throw new Error('No model.');
      // Send every grid cell, including explicit zeros — the backend upsert sets
      // onHand to whatever we send. Dropping zeros would make it impossible to
      // bring an existing stock level back down to 0 (the row would just be left
      // untouched). Untouched cells aren't in the grid, so they're never sent.
      const levels = Object.entries(grid).map(([key, qty]) => {
        const [productId, locationId] = key.split('::');
        return { productId: productId!, locationId: locationId!, qty: Number(qty) || 0 };
      });
      await apiPost(`/product-models/${modelId}/opening-stock`, { levels });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['product-models'] });
      qc.invalidateQueries({ queryKey: ['products-admin'] });
      if (isEdit) flashSaved();
      else finish();
    },
    onError: (e) => setError(getApiError(e).message),
  });

  function finish(): void {
    qc.invalidateQueries({ queryKey: ['product-models-incomplete'] });
    onClose();
  }

  // Cancel: discard an empty just-created model (no variants) so it doesn't linger.
  const cancel = async (): Promise<void> => {
    if (modelId && savedCount === 0) {
      try {
        await apiDelete(`/product-models/${modelId}`);
      } catch {
        /* best-effort */
      }
    }
    qc.invalidateQueries({ queryKey: ['product-models-incomplete'] });
    onClose();
  };

  const step1Valid = !!name.trim() && !!categoryId;
  const step2Valid = savedCount + newValidRows.length > 0 && !dupSkus;
  const busy = saveStep1.isPending || saveStep2.isPending || saveStep3.isPending;

  const variantLabel = (v: {
    size: string | null;
    fabric: string | null;
    color?: string | null;
    sideColor?: string | null;
    density?: string | null;
    sku: string;
  }): string =>
    [v.size, v.fabric, v.color, v.sideColor, v.density].filter(Boolean).join(' · ') || v.sku;

  return (
    <Modal
      title={
        !isEdit
          ? 'New product model'
          : startStep === 2
            ? 'Finish product model'
            : 'Edit product model'
      }
      subtitle={
        <StepBar
          step={step}
          lastStep={lastStep}
          labels={isEdit ? EDIT_TABS : STEPS}
          onJump={
            isEdit
              ? (n) => {
                  setError(null);
                  setSavedFlash(false);
                  setStep(n);
                }
              : undefined
          }
        />
      }
      size="3xl"
      onClose={() => (isEdit ? onClose() : void cancel())}
      footer={
        isEdit ? (
          // Edit = freely-navigable tabs: save the CURRENT section in place, no
          // forced advance. Close never deletes the (existing) model.
          <>
            <Button variant="secondary" onClick={() => onClose()} disabled={busy}>
              Close
            </Button>
            {step === 1 && (
              <Button
                loading={saveStep1.isPending}
                disabled={!step1Valid}
                onClick={() => saveStep1.mutate()}
              >
                Save details
              </Button>
            )}
            {step === 2 && (
              <Button
                loading={saveStep2.isPending}
                disabled={!step2Valid}
                onClick={() => saveStep2.mutate()}
              >
                Save models
              </Button>
            )}
            {step === 3 && (
              <Button loading={saveStep3.isPending} onClick={() => saveStep3.mutate()}>
                Save stock
              </Button>
            )}
          </>
        ) : (
          <>
            {step > 1 ? (
              <Button
                variant="secondary"
                onClick={() => {
                  setError(null);
                  setStep((s) => s - 1);
                }}
                disabled={busy}
              >
                ← Back
              </Button>
            ) : (
              <Button variant="secondary" onClick={() => void cancel()} disabled={busy}>
                Cancel
              </Button>
            )}
            {step === 1 && (
              <Button
                loading={saveStep1.isPending}
                disabled={!step1Valid}
                onClick={() => saveStep1.mutate()}
              >
                Save &amp; continue →
              </Button>
            )}
            {step === 2 && (
              <Button
                loading={saveStep2.isPending}
                disabled={!step2Valid}
                onClick={() => saveStep2.mutate()}
              >
                {lastStep === 2 ? 'Save & finish' : 'Save & continue →'}
              </Button>
            )}
            {step === 3 && (
              <Button loading={saveStep3.isPending} onClick={() => saveStep3.mutate()}>
                Save &amp; finish
              </Button>
            )}
          </>
        )
      }
    >
      <div className="space-y-5">
        {/* ── Step 1: Product ─────────────────────────────────────────────── */}
        {step === 1 && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Input
                label="Model name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="md:col-span-2"
                placeholder="e.g. Euroflex"
              />
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">
                  Category
                </span>
                <select
                  value={categoryId}
                  onChange={(e) => pickCategory(e.target.value)}
                  className="kc-input"
                  disabled={!!modelId}
                >
                  <option value="">— pick category —</option>
                  {(cats.data ?? []).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <Input
              label="Description (optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input
                label="Gift note (optional)"
                value={gift}
                onChange={(e) => setGift(e.target.value)}
                placeholder="e.g. Free pillows with purchase"
                hint="Free-text shown on the catalogue. For stock-deducting gifts use the picker below."
              />
              <Input
                label="Warranty (optional)"
                value={warranty}
                onChange={(e) => setWarranty(e.target.value)}
                placeholder="e.g. 10 Years on Springs"
                hint="Warranty period shown on the catalogue."
              />
            </div>
            <div className="rounded-md border border-rule bg-paper-2/40 p-3">
              <GiftEditor value={gifts} onChange={setGifts} />
            </div>
            <div>
              <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">
                Image (optional)
              </span>
              <div className="mt-1 flex items-center gap-3">
                {imagePreview ? (
                  <img
                    src={imagePreview}
                    alt=""
                    className="w-16 h-16 rounded object-cover border border-rule"
                  />
                ) : (
                  <div className="w-16 h-16 rounded bg-paper-2 border border-rule flex items-center justify-center text-ink-4 text-[10px]">
                    no img
                  </div>
                )}
                <div className="space-y-1">
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/avif,image/gif,image/svg+xml"
                    onChange={(e) => pickImage(e.target.files?.[0] ?? null)}
                    className="block text-sm text-ink-2 file:mr-3 file:px-3 file:py-1.5 file:rounded file:border-0 file:bg-accent file:text-white file:cursor-pointer hover:file:bg-accent-2 file:transition"
                  />
                  <div className="text-[11px] text-ink-3">
                    {imageFile ? (
                      <Button
                        type="button"
                        variant="tertiary-danger"
                        size="xs"
                        onClick={() => pickImage(null)}
                      >
                        Remove
                      </Button>
                    ) : (
                      'Shown on the catalogue card. You can add more later from the model page.'
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* ── Step 2: Sizes / variants ────────────────────────────────────── */}
        {step === 2 && (
          <>
            {pricedAxesList.length > 0 && (
              <div className="rounded-md border border-rule bg-paper-2/40 p-3 space-y-2">
                {pricedAxesList.map(({ axis, label, suggestions }) => (
                  <ChipEditor
                    key={axis}
                    label={`${label} — define values, then pick per row below`}
                    values={axisValues[axis]}
                    onChange={setAxisValues[axis]}
                    onRename={(oldValue, newValue) => {
                      // Rename the chip (dedupe if the new name already exists)…
                      setAxisValues[axis](
                        axisValues[axis]
                          .map((x) => (x === oldValue ? newValue : x))
                          .filter((x, i, a) => a.indexOf(x) === i),
                      );
                      // …and repoint every row that picked the old value.
                      setRows((rs) =>
                        rs.map((r) => (r[axis] === oldValue ? { ...r, [axis]: newValue } : r)),
                      );
                    }}
                    suggestions={suggestions}
                  />
                ))}
                <div className="text-[11px] text-ink-3">
                  Each size + finish combination is its own item with its own code, price and stock.
                  Tip: price one colour for a size, then “Generate combinations” — the other colours
                  inherit the same price (price follows size + fabric, not colour).
                </div>
              </div>
            )}
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">
                Sizes — each is its own barcoded stock unit
              </span>
              <div className="flex gap-2 items-center">
                {pricedCols.length > 0 && (
                  <Button size="sm" variant="ghost" onClick={genCombos}>
                    + Generate combinations
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  loading={autofillSkus.isPending}
                  disabled={blankSkuIdx.length === 0}
                  title={
                    blankSkuIdx.length === 0
                      ? 'No blank codes to fill'
                      : 'Fill blank codes with running numbers (e.g. BED-00042)'
                  }
                  onClick={() => autofillSkus.mutate()}
                >
                  Auto-fill codes
                </Button>
                <Button size="sm" variant="ghost" onClick={addStandardSizes}>
                  + Standard sizes
                </Button>
                <Button size="sm" variant="secondary" onClick={() => addRow()}>
                  + Add size
                </Button>
              </div>
            </div>
            <div className="overflow-x-auto border border-rule rounded-md">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wide text-ink-3 bg-paper-2 border-b border-rule">
                    <th className="px-2 py-2 font-medium">Size</th>
                    {pricedCols.map((c) => (
                      <th key={c.axis} className="px-2 py-2 font-medium">
                        {c.col}
                      </th>
                    ))}
                    <th className="px-2 py-2 font-medium">Code / SKU *</th>
                    <th className="px-2 py-2 font-medium">Barcode</th>
                    <th className="px-2 py-2 font-medium text-right">Base price *</th>
                    <th className="px-2 py-2 font-medium text-right">Promo price</th>
                    <th className="w-8" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => {
                    const skuLocked = !!r.id; // SKU is identity — fixed once saved; other fields stay editable
                    return (
                      <tr key={r.id ?? `new-${i}`} className="border-b border-rule-2 last:border-0">
                        <td className="px-2 py-1.5">
                          <input
                            className="kc-input kc-input-sm w-24"
                            value={r.size}
                            onChange={(e) => setRow(i, { size: e.target.value })}
                            placeholder="160*200"
                          />
                        </td>
                        {pricedCols.map((c) => (
                          <td key={c.axis} className="px-2 py-1.5">
                            <select
                              className="kc-input kc-input-sm w-28"
                              value={r[c.axis]}
                              onChange={(e) =>
                                setRow(i, { [c.axis]: e.target.value } as Partial<VariantRow>)
                              }
                            >
                              <option value="">—</option>
                              {axisValues[c.axis].map((v) => (
                                <option key={v} value={v}>
                                  {v}
                                </option>
                              ))}
                            </select>
                          </td>
                        ))}
                        <td className="px-2 py-1.5">
                          <input
                            className={cn(
                              'kc-input kc-input-sm w-28 kc-mono',
                              dupRow(rows, r) && 'border-negative',
                            )}
                            value={r.sku}
                            disabled={skuLocked}
                            title={skuLocked ? 'Code is permanent' : undefined}
                            onChange={(e) => setRow(i, { sku: e.target.value })}
                            placeholder="P-0001"
                          />
                        </td>
                        <td className="px-2 py-1.5">
                          <input
                            className="kc-input kc-input-sm w-32 kc-mono"
                            value={r.barcode}
                            onChange={(e) => setRow(i, { barcode: e.target.value })}
                            placeholder="optional"
                          />
                        </td>
                        <td className="px-2 py-1.5 align-top">
                          <VatPricePair
                            size="sm"
                            rate={WIZARD_VAT}
                            valueIncl={r.basePrice}
                            onChangeIncl={(v) => setRow(i, { basePrice: v })}
                          />
                        </td>
                        <td className="px-2 py-1.5 align-top">
                          <VatPricePair
                            size="sm"
                            rate={WIZARD_VAT}
                            valueIncl={r.salePrice}
                            onChangeIncl={(v) => setRow(i, { salePrice: v })}
                            placeholder="—"
                          />
                        </td>
                        <td className="px-1 py-1.5 text-center">
                          <IconButton
                            onClick={() => removeRow.mutate(i)}
                            variant="danger"
                            size="sm"
                            aria-label="Remove size"
                          >
                            ×
                          </IconButton>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {dupSkus && (
              <div className="text-xs text-negative mt-1">
                Duplicate codes — each size needs a unique code.
              </div>
            )}
            <div className="text-[11px] text-ink-3">
              Prices at VAT 15% — type excl. or incl. VAT; the other fills in. The incl.-VAT price
              is stored. Existing sizes are editable; the code (SKU) is permanent.
            </div>
          </>
        )}

        {/* ── Step 3: Opening stock per location ──────────────────────────── */}
        {step === 3 && (
          <>
            <div className="text-xs text-ink-3">
              Opening stock on hand now, per location. Leave 0 if none.
              {madeToOrder &&
                ' Made-to-order items are usually 0 — use this only for display/existing units.'}
            </div>
            <div className="overflow-x-auto border border-rule rounded-md">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wide text-ink-3 bg-paper-2 border-b border-rule">
                    <th className="px-3 py-2 font-medium">Variant</th>
                    {(locations.data ?? []).map((l) => (
                      <th key={l.id} className="px-3 py-2 font-medium text-right">
                        {l.shop ? l.shop.code : l.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {savedVariants.map((v) => (
                    <tr key={v.id} className="border-b border-rule-2 last:border-0">
                      <td className="px-3 py-2 text-ink" title={v.sku}>
                        {variantLabel({
                          size: v.size || null,
                          fabric: v.fabric || null,
                          color: v.color || null,
                          sideColor: v.sideColor || null,
                          density: v.density || null,
                          sku: v.sku,
                        })}{' '}
                        <span className="text-ink-4 kc-mono text-xs">{v.sku}</span>
                      </td>
                      {(locations.data ?? []).map((l) => {
                        const key = `${v.id}::${l.id}`;
                        return (
                          <td key={l.id} className="px-3 py-2 text-right">
                            <input
                              type="number"
                              min="0"
                              step="1"
                              className="kc-input kc-input-sm w-20 text-right"
                              value={grid[key] ?? ''}
                              onChange={(e) => setGrid((g) => ({ ...g, [key]: e.target.value }))}
                              placeholder="0"
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {savedVariants.length === 0 && (
              <div className="text-xs text-ink-3 italic">
                No saved sizes yet — go back and add sizes first.
              </div>
            )}
          </>
        )}

        {error && <div className="text-xs text-negative">{error}</div>}
        {savedFlash && !error && <div className="text-xs text-accent">Saved ✓</div>}
      </div>
    </Modal>
  );
}

function variantToRow(v: ModelDetail['variants'][number]): VariantRow {
  return {
    id: v.id,
    size: v.size ?? '',
    fabric: v.fabric ?? '',
    color: v.color ?? '',
    sideColor: v.sideColor ?? '',
    density: v.density ?? '',
    sku: v.sku,
    barcode: v.barcode ?? '',
    basePrice: v.basePrice,
    salePrice: v.salePrice ?? '',
  };
}
function buildGrid(variants: ModelDetail['variants']): Record<string, string> {
  const g: Record<string, string> = {};
  for (const v of variants)
    for (const lv of v.stockLevels) g[`${v.id}::${lv.location.id}`] = String(lv.onHand);
  return g;
}
const uniq = <T,>(arr: T[]): T[] => [...new Set(arr)];
function dupRow(rows: VariantRow[], row: VariantRow): boolean {
  if (!row.sku.trim()) return false;
  return rows.filter((r) => r.sku.trim() === row.sku.trim()).length > 1;
}

// Step indicator. In the create wizard it's a read-only progress bar; in edit
// mode (`onJump` provided) each step is a clickable tab for free navigation.
function StepBar({
  step,
  lastStep,
  labels = STEPS,
  onJump,
}: {
  step: number;
  lastStep: number;
  labels?: readonly string[];
  onJump?: (n: number) => void;
}): JSX.Element {
  const clickable = !!onJump;
  return (
    <span className="inline-flex items-center gap-2 text-xs">
      {labels.slice(0, lastStep).map((label, idx) => {
        const n = idx + 1;
        const active = n === step;
        const done = n < step;
        const inner = (
          <span
            className={cn(
              'inline-flex items-center gap-1.5',
              active
                ? 'text-accent font-medium'
                : clickable
                  ? 'text-ink-2 hover:text-accent'
                  : done
                    ? 'text-ink-2'
                    : 'text-ink-4',
            )}
          >
            <span
              className={cn(
                'w-4 h-4 rounded-full text-[10px] flex items-center justify-center',
                active
                  ? 'bg-accent text-white'
                  : done && !clickable
                    ? 'bg-ink-3 text-white'
                    : 'bg-paper-2 border border-rule',
              )}
            >
              {done && !clickable ? '✓' : n}
            </span>
            {label}
          </span>
        );
        return (
          <span key={label} className="inline-flex items-center gap-2">
            {clickable ? (
              <button
                type="button"
                onClick={() => onJump?.(n)}
                className="inline-flex items-center rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
              >
                {inner}
              </button>
            ) : (
              inner
            )}
            {n < lastStep && <span className="text-ink-4">›</span>}
          </span>
        );
      })}
    </span>
  );
}

// Links gift products (auto-deducted from stock when the model sells). A gift is
// either a specific variant (one fixed size) or a whole MODEL — for a multi-size
// model gift the sales girl picks which size to give at order time.
function GiftEditor({
  value,
  onChange,
}: {
  value: GiftRow[];
  onChange: (v: GiftRow[]) => void;
}): JSX.Element {
  // Which kind of gift the search adds: a single variant, or a multi-size model.
  const [mode, setMode] = useState<'product' | 'model'>('product');
  const [q, setQ] = useState('');
  const productResults = useQuery({
    queryKey: ['gift-product-search', q],
    queryFn: () => apiGet<Array<{ id: string; sku: string; name: string }>>('/products', { q }),
    enabled: mode === 'product' && q.trim().length >= 2,
  });
  const modelResults = useQuery({
    queryKey: ['gift-model-search', q],
    queryFn: () =>
      apiGet<Array<{ id: string; name: string; variantCount: number }>>('/product-models', { q }),
    enabled: mode === 'model' && q.trim().length >= 2,
  });
  const addProduct = (p: { id: string; name: string }): void => {
    if (!value.some((g) => g.productId === p.id))
      onChange([...value, { productId: p.id, giftModelId: null, qty: 1, name: p.name }]);
    setQ('');
  };
  const addModel = (m: { id: string; name: string }): void => {
    if (!value.some((g) => g.giftModelId === m.id))
      onChange([...value, { productId: null, giftModelId: m.id, qty: 1, name: m.name }]);
    setQ('');
  };
  const products = (productResults.data ?? []).filter((p) => !value.some((g) => g.productId === p.id));
  const models = (modelResults.data ?? []).filter((m) => !value.some((g) => g.giftModelId === m.id));
  return (
    <div>
      <div className="text-xs font-medium text-ink-3 uppercase tracking-wide">
        Gift products — deducted from stock on sale
      </div>
      {value.length > 0 && (
        <ul className="mt-2 space-y-1.5">
          {value.map((g, i) => (
            <li key={g.giftModelId ?? g.productId ?? i} className="flex items-center gap-2 text-sm">
              <span className="flex-1 text-ink">
                {g.name}
                {g.giftModelId && (
                  <span className="ml-1.5 text-[10px] uppercase tracking-wider bg-accent-soft text-accent px-1.5 py-0.5 rounded align-middle">
                    Size chosen at sale
                  </span>
                )}
              </span>
              <span className="text-ink-4 text-xs">qty</span>
              <input
                type="number"
                min="1"
                step="1"
                className="kc-input kc-input-sm w-16 text-right"
                value={g.qty}
                onChange={(e) =>
                  onChange(
                    value.map((x, idx) =>
                      idx === i ? { ...x, qty: Math.max(1, Number(e.target.value) || 1) } : x,
                    ),
                  )
                }
              />
              <IconButton
                type="button"
                onClick={() => onChange(value.filter((_, idx) => idx !== i))}
                variant="danger"
                size="sm"
                aria-label="Remove gift"
              >
                ×
              </IconButton>
            </li>
          ))}
        </ul>
      )}
      {/* Toggle between gifting a fixed variant and gifting a whole model. */}
      <div className="mt-3 flex gap-1 text-xs">
        {(['product', 'model'] as const).map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => {
              setMode(m);
              setQ('');
            }}
            className={`px-2.5 py-1 rounded border ${
              mode === m
                ? 'border-accent text-accent bg-accent-soft'
                : 'border-rule text-ink-3 hover:bg-paper-2'
            }`}
          >
            {m === 'product' ? 'Specific size' : 'Any size (pick at sale)'}
          </button>
        ))}
      </div>
      <div className="mt-2">
        <input
          className="kc-input kc-input-sm w-full"
          placeholder={
            mode === 'product'
              ? 'Search a product to add as a gift…'
              : 'Search a model — the sales girl picks the size…'
          }
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        {mode === 'product' && q.trim().length >= 2 && products.length > 0 && (
          <div className="mt-1 border border-rule rounded bg-surface max-h-40 overflow-y-auto">
            {products.slice(0, 8).map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => addProduct(p)}
                className="block w-full text-left px-2 py-1.5 text-sm hover:bg-paper-2"
              >
                {p.name} <span className="text-ink-4 kc-mono text-xs">{p.sku}</span>
              </button>
            ))}
          </div>
        )}
        {mode === 'model' && q.trim().length >= 2 && models.length > 0 && (
          <div className="mt-1 border border-rule rounded bg-surface max-h-40 overflow-y-auto">
            {models.slice(0, 8).map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => addModel(m)}
                className="block w-full text-left px-2 py-1.5 text-sm hover:bg-paper-2"
              >
                {m.name}{' '}
                <span className="text-ink-4 text-xs">
                  {m.variantCount} size{m.variantCount === 1 ? '' : 's'}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="text-[11px] text-ink-3 mt-1.5">
        Added free to the order (qty × units bought) and deducted from the gift's stock. Skipped if
        out of stock. For an “any size” model gift, the sales girl chooses the size on the order.
      </div>
    </div>
  );
}

function ChipEditor({
  label,
  values,
  onChange,
  onRename,
  suggestions,
}: {
  label: string;
  values: string[];
  onChange: (v: string[]) => void;
  /** Rename a value in place — lets the parent also repoint any rows using it. */
  onRename?: (oldValue: string, newValue: string) => void;
  suggestions: string[];
}): JSX.Element {
  const [draft, setDraft] = useState('');
  const [editing, setEditing] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const add = (v: string): void => {
    const t = v.trim().toLowerCase();
    if (t && !values.includes(t)) onChange([...values, t]);
    setDraft('');
  };
  const startEdit = (v: string): void => {
    setEditing(v);
    setEditDraft(v);
  };
  const commitEdit = (): void => {
    const old = editing;
    if (old == null) return;
    setEditing(null);
    const next = editDraft.trim().toLowerCase();
    if (!next || next === old) return;
    if (onRename) onRename(old, next);
    // Fall back to a plain value swap (dedupes if the new name already exists).
    else onChange(values.map((x) => (x === old ? next : x)).filter((x, i, a) => a.indexOf(x) === i));
  };
  const unused = suggestions.filter((s) => !values.includes(s));
  return (
    <div>
      {label && <div className="text-[11px] text-ink-3 mb-1">{label}</div>}
      <div className="flex flex-wrap items-center gap-1.5">
        {values.map((v) =>
          editing === v ? (
            <input
              key={v}
              autoFocus
              className="kc-input kc-input-sm w-24"
              value={editDraft}
              onChange={(e) => setEditDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitEdit();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  setEditing(null);
                }
              }}
              onBlur={commitEdit}
            />
          ) : (
            <span
              key={v}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-surface border border-rule text-xs text-ink"
            >
              <button
                type="button"
                className="cursor-text hover:underline"
                onClick={() => startEdit(v)}
                title="Click to rename"
              >
                {v}
              </button>
              <IconButton
                onClick={() => onChange(values.filter((x) => x !== v))}
                variant="danger"
                size="sm"
                aria-label={`Remove ${v}`}
              >
                ×
              </IconButton>
            </span>
          ),
        )}
        <input
          className="kc-input kc-input-sm w-24"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add(draft);
            }
          }}
          placeholder="add…"
        />
        {unused.length > 0 && (
          <span className="text-[10px] text-ink-3">
            suggest:{' '}
            {unused.map((s) => (
              <Button key={s} variant="tertiary" size="xs" onClick={() => add(s)} className="mr-1">
                {s}
              </Button>
            ))}
          </span>
        )}
      </div>
    </div>
  );
}
